import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlassIcon, MapPinIcon, EnvelopeIcon, UserIcon, StarIcon, CurrencyPoundIcon, FunnelIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import PublicLayoutWrapper from '../components/PublicLayoutWrapper';
import config from '../config';

const apiBaseUrl = config.apiBaseUrl;

const SERVICE_OPTIONS = [
    { key: 'diet_meal_plans',      label: 'Diet / Meal Plans' },
    { key: 'sports_massage',       label: 'Sports Massage' },
    { key: 'consultations',        label: 'Consultations' },
    { key: 'online_coaching',      label: 'Online Coaching' },
    { key: 'group_sessions',       label: 'Group Sessions' },
    { key: 'nutrition_advice',     label: 'Nutrition Advice' },
    { key: 'body_composition',     label: 'Body Composition Testing' },
    { key: 'rehabilitation',       label: 'Rehabilitation / Injury Support' },
    { key: 'pre_post_natal',       label: 'Pre / Post Natal' },
    { key: 'mindset_coaching',     label: 'Mindset & Wellbeing Coaching' },
    { key: 'strength_conditioning',label: 'Strength & Conditioning' },
    { key: 'youth_training',       label: 'Youth / Junior Training' },
];

const formatPrice = (pt) => {
    if (!pt.priceType || !pt.priceFrom) return null;
    if (pt.priceType === 'from') return `From £${pt.priceFrom}`;
    if (pt.priceType === 'range' && pt.priceTo) return `£${pt.priceFrom} – £${pt.priceTo}`;
    return `From £${pt.priceFrom}`;
};

const FindPT = () => {
    const [listings, setListings] = useState([]);
    const [cities, setCities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [showServiceFilter, setShowServiceFilter] = useState(false);
    const [contactModal, setContactModal] = useState(null);
    const [contactEmail, setContactEmail] = useState('');
    const [contactStatus, setContactStatus] = useState('');
    const [contactLoading, setContactLoading] = useState(false);
    const [showSplash, setShowSplash] = useState(true);
    const [splashServices, setSplashServices] = useState([]);
    const [splashCity, setSplashCity] = useState('');

    useEffect(() => {
        const fetchListings = async () => {
            try {
                const response = await fetch(`${apiBaseUrl}/api/ptdirectory`);
                if (response.ok) {
                    const data = await response.json();
                    setListings(data.listings || []);
                    setCities(data.cities || []);
                }
            } catch (err) {
                console.error('Error fetching PT listings:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchListings();
    }, []);

    const filteredListings = useMemo(() => {
        return listings.filter(pt => {
            const matchesSearch = !searchTerm ||
                pt.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pt.bio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pt.specialisms?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                pt.city?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesCity = !selectedCity || pt.city === selectedCity;
            const ptServiceList = pt.services
                ? pt.services.split(',').map(s => s.trim())
                : [];
            const matchesService = selectedServices.length === 0 ||
                selectedServices.some(svc => ptServiceList.includes(svc));
            return matchesSearch && matchesCity && matchesService;
        });
    }, [listings, searchTerm, selectedCity, selectedServices]);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!contactEmail.trim()) return;
        setContactLoading(true);
        setContactStatus('');
        try {
            const response = await fetch(`${apiBaseUrl}/api/ptdirectory/contact`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ptDirectoryListingId: contactModal.listingId,
                    enquirerEmail: contactEmail.trim()
                })
            });
            if (response.ok) {
                setContactStatus('success');
                setContactEmail('');
            } else {
                setContactStatus('error');
            }
        } catch {
            setContactStatus('error');
        } finally {
            setContactLoading(false);
        }
    };

    const openContactModal = (listing) => {
        setContactModal({ listingId: listing.id, ptName: listing.name });
        setContactEmail('');
        setContactStatus('');
    };

    const closeContactModal = () => {
        setContactModal(null);
        setContactEmail('');
        setContactStatus('');
    };

    const serviceLabels = selectedServices.length > 0
        ? selectedServices.map(k => SERVICE_OPTIONS.find(s => s.key === k)?.label).filter(Boolean)
        : [];

    const toggleService = (key) => {
        setSelectedServices(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const toggleSplashService = (key) => {
        setSplashServices(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleSplashSubmit = (e) => {
        e.preventDefault();
        if (splashServices.length > 0) setSelectedServices(splashServices);
        if (splashCity.trim()) setSelectedCity(splashCity.trim());
        setShowSplash(false);
    };

    const handleSplashSkip = () => {
        setShowSplash(false);
    };

    return (
        <PublicLayoutWrapper
            title="Find a Personal Trainer Near You"
            subtitle="Browse certified personal trainers in your area"
            showBackToHome={true}
        >
            {/* Splash Screen Overlay */}
            {showSplash && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
                <div className="bg-gray-900 border border-emerald-600/40 rounded-2xl p-8 w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
                        {/* Icon & Heading */}
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600/20 rounded-full mb-4">
                                <MagnifyingGlassIcon className="h-8 w-8 text-emerald-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Find Your Perfect PT</h2>
                            <p className="text-gray-400 text-sm">
                                Tell us what you're looking for and where, and we'll show you the best matches.
                            </p>
                        </div>

                        <form onSubmit={handleSplashSubmit} className="space-y-4">
                            {/* What are you looking for? */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-3">
                                    What are you looking for? <span className="text-gray-500 font-normal">(select all that apply)</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {SERVICE_OPTIONS.map(({ key, label }) => (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => toggleSplashService(key)}
                                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                                splashServices.includes(key)
                                                    ? 'border-emerald-500 bg-emerald-900/40 text-emerald-300'
                                                    : 'border-gray-600 text-gray-300 hover:border-emerald-500 hover:text-emerald-300'
                                            }`}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                {splashServices.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setSplashServices([])}
                                        className="mt-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
                                    >
                                        × Clear all selections ({splashServices.length} selected)
                                    </button>
                                )}
                            </div>

                            {/* Which area? */}
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Which area are you in?
                                </label>
                                <div className="relative">
                                    <MapPinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                                    <select
                                        value={splashCity}
                                        onChange={(e) => setSplashCity(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none"
                                    >
                                        <option value="">All locations</option>
                                        {cities.map(city => (
                                            <option key={city} value={city}>{city}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex flex-col gap-3 pt-2">
                                <button
                                    type="submit"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                                >
                                    <MagnifyingGlassIcon className="h-5 w-5" />
                                    Search for a PT
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSplashSkip}
                                    className="w-full text-gray-400 hover:text-gray-200 text-sm py-2 transition-colors"
                                >
                                    Browse all trainers
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Search and Filter */}
            <div className="mb-8 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Text search */}
                    <div className="flex-1 relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, specialisms, or location..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                    </div>
                    {/* City filter */}
                    <div className="sm:w-48">
                        <select
                            value={selectedCity}
                            onChange={(e) => setSelectedCity(e.target.value)}
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        >
                            <option value="">All Locations</option>
                            {cities.map(city => (
                                <option key={city} value={city}>{city}</option>
                            ))}
                        </select>
                    </div>
                    {/* Service filter toggle */}
                    <button
                        onClick={() => setShowServiceFilter(v => !v)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
                            selectedServices.length > 0
                                ? 'border-emerald-500 bg-emerald-900/20 text-emerald-300'
                                : 'border-gray-600 bg-gray-800 text-gray-300 hover:border-gray-500'
                        }`}
                    >
                        <FunnelIcon className="h-4 w-4" />
                        {selectedServices.length > 0 ? `${selectedServices.length} service${selectedServices.length > 1 ? 's' : ''} selected` : 'Filter by Service'}
                    </button>
                </div>

                {/* Service filter panel */}
                {showServiceFilter && (
                    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-300">Filter by service offered <span className="text-gray-500 font-normal">(select all that apply)</span></span>
                            {selectedServices.length > 0 && (
                                <button
                                    onClick={() => setSelectedServices([])}
                                    className="text-xs text-emerald-400 hover:text-emerald-300"
                                >
                                    Clear all ({selectedServices.length})
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {SERVICE_OPTIONS.map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => toggleService(key)}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                        selectedServices.includes(key)
                                            ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300'
                                            : 'border-gray-600 text-gray-300 hover:border-gray-400'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <p className="text-gray-400 text-sm">
                    {loading ? 'Loading...' : `${filteredListings.length} trainer${filteredListings.length !== 1 ? 's' : ''} found`}
                    {selectedServices.length > 0 && (
                        <span className="ml-2 text-emerald-400">
                            · {serviceLabels.join(', ')}
                            <button onClick={() => setSelectedServices([])} className="ml-1 text-gray-500 hover:text-gray-300">×</button>
                        </span>
                    )}
                </p>
            </div>

            {/* PT Listings */}
            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-400"></div>
                </div>
            ) : filteredListings.length === 0 ? (
                <div className="text-center py-12">
                    <UserIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg mb-2">No personal trainers found.</p>
                    <p className="text-gray-500 text-sm">Try adjusting your search or filters.</p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredListings.map(pt => {
                        const priceDisplay = formatPrice(pt);
                        const ptServices = pt.services
                            ? pt.services.split(',').map(s => s.trim()).filter(Boolean)
                            : [];
                        return (
                            <div key={pt.id} className="bg-gray-900 rounded-lg border border-gray-700 p-6 flex flex-col">
                                {/* Header */}
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-emerald-600/20 rounded-full flex items-center justify-center flex-shrink-0">
                                            <UserIcon className="h-6 w-6 text-emerald-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-white font-semibold text-lg leading-tight">{pt.name}</h3>
                                            {pt.isFounderMember && (
                                                <span className="inline-flex items-center gap-1 text-xs text-yellow-400 font-medium">
                                                    <StarIcon className="h-3 w-3" />
                                                    Foundation Member
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Location */}
                                <div className="flex items-center text-gray-400 text-sm mb-2">
                                    <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                    <span>{pt.city}{pt.postcode ? `, ${pt.postcode}` : ''}</span>
                                </div>

                                {/* Price */}
                                {priceDisplay && (
                                    <div className="flex items-center text-sm mb-3 text-emerald-400 font-medium">
                                        <CurrencyPoundIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                                        {priceDisplay} per session
                                    </div>
                                )}

                                {/* Specialisms */}
                                {pt.specialisms && (
                                    <div className="mb-3">
                                        <div className="flex flex-wrap gap-1">
                                            {pt.specialisms.split(',').map((s, i) => (
                                                <span key={i} className="text-xs bg-emerald-900/30 text-emerald-300 border border-emerald-800/50 px-2 py-1 rounded-full">
                                                    {s.trim()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Services */}
                                {ptServices.length > 0 && (
                                    <div className="mb-3">
                                        <div className="flex flex-wrap gap-1">
                                            {ptServices.map(key => {
                                                const svc = SERVICE_OPTIONS.find(s => s.key === key);
                                                if (!svc) return null;
                                                return (
                                                    <span
                                                        key={key}
                                                        className="text-xs bg-blue-900/20 text-blue-300 border border-blue-800/40 px-2 py-1 rounded-full"
                                                    >
                                                        {svc.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Bio */}
                                {pt.bio && (
                                    <p className="text-gray-400 text-sm mb-4 flex-1 leading-relaxed">
                                        {pt.bio.length > 150 ? `${pt.bio.substring(0, 150)}…` : pt.bio}
                                    </p>
                                )}

                                {/* Action Buttons */}
                                <div className="mt-auto flex flex-col gap-2">
                                    {pt.slug && (
                                        <Link
                                            to={`/pt/${pt.slug}`}
                                            className="w-full flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
                                        >
                                            <ArrowRightIcon className="h-4 w-4" />
                                            View Profile
                                        </Link>
                                    )}
                                    <button
                                        onClick={() => openContactModal(pt)}
                                        className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 px-4 rounded-lg font-medium transition-colors"
                                    >
                                        <EnvelopeIcon className="h-4 w-4" />
                                        Contact Me
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Join Directory CTA */}
            <div className="mt-12 bg-gradient-to-r from-emerald-900/20 to-blue-900/20 border border-emerald-600/30 rounded-lg p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Are you a Personal Trainer?</h3>
                <p className="text-gray-300 mb-2">
                    Join our PT Directory and let potential clients find you.
                </p>
                <p className="text-gray-400 text-sm mb-6">
                    From just <span className="text-emerald-400 font-semibold line-through">£3.99/month</span> grab a limited offer <span className="text-yellow-400 font-semibold">Foundation Member</span> spot at <span className="text-yellow-400 font-semibold">£1.99/month</span>
                </p>
                <Link
                    to="/plans?from=find-pt"
                    className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
                >
                    Be Part of the Directory
                </Link>
            </div>

            {/* Contact Modal */}
            {contactModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeContactModal}>
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <h3 className="text-xl font-semibold text-white mb-2">Contact {contactModal.ptName}</h3>
                        <p className="text-gray-400 text-sm mb-4">
                            Enter your email address and we'll let {contactModal.ptName} know you'd like to get in touch.
                        </p>

                        {contactStatus === 'success' ? (
                            <div className="text-center py-4">
                                <div className="text-emerald-400 text-4xl mb-3">✓</div>
                                <p className="text-emerald-300 font-medium mb-1">Enquiry sent!</p>
                                <p className="text-gray-400 text-sm">{contactModal.ptName} will be in touch soon.</p>
                                <button
                                    onClick={closeContactModal}
                                    className="mt-4 bg-gray-700 hover:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleContactSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-300 mb-2">
                                        Your Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        required
                                        placeholder="your@email.com"
                                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                    />
                                </div>

                                {contactStatus === 'error' && (
                                    <p className="text-red-400 text-sm mb-4">Something went wrong. Please try again.</p>
                                )}

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={closeContactModal}
                                        className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={contactLoading || !contactEmail.trim()}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white py-2.5 rounded-lg font-medium transition-colors"
                                    >
                                        {contactLoading ? 'Sending…' : 'Send Enquiry'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </PublicLayoutWrapper>
    );
};

export default FindPT;

